import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  type User,
} from 'firebase/auth';
import { auth, firebaseEnabled } from './firebase';

interface AuthState {
  /** Currently signed-in Firebase user, or null when in debug mode / not signed in. */
  user: User | null;
  /** True while the initial auth state is being resolved. */
  loading: boolean;
  /** True when Firebase auth is active (env vars configured). */
  isFirebase: boolean;
  /** Get a Bearer token for API calls. In Firebase mode returns an ID token; in debug mode returns the debug UID. */
  getToken: () => Promise<string>;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(firebaseEnabled);

  useEffect(() => {
    if (!auth) return;
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return unsub;
  }, []);

  async function getToken(): Promise<string> {
    if (user) {
      return user.getIdToken();
    }
    // Debug mode — return empty string; client.ts will use the debug UID header instead.
    return '';
  }

  async function signInWithGoogle() {
    if (!auth) return;
    const provider = new GoogleAuthProvider();
    await signInWithPopup(auth, provider);
  }

  async function signInWithEmail(email: string, password: string) {
    if (!auth) return;
    await signInWithEmailAndPassword(auth, email, password);
  }

  async function signOut() {
    if (!auth) return;
    await firebaseSignOut(auth);
  }

  return (
    <AuthContext.Provider value={{ user, loading, isFirebase: firebaseEnabled, getToken, signInWithGoogle, signInWithEmail, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
